import { Request } from '../request';
import {
    TAddStaff,
    TConfirmBatchParams,
    TImportDraftListParams,
    TUpdateImportDraftReq,
    TInviteProvidersParams,
    TPracticePrescriptionListFilter,
    TReassignExecuteReq,
    TScopedProviderOptionsParams,
    TTeamMemberListPageParams,
} from 'types/group';
import { TGPSendMessageParams, TGPGetMessagesParams } from 'types/inbox';
import { IGetKlarityReviewListInput } from 'types/reputation';
import { IDisputeSubmitInput, TKlarityReviewReplyInput } from 'types/survey';
import {
    TAttentionAlertType,
    TPracticeOverviewByDimReq,
    TPracticeOverviewFutureByDimReq,
    TPracticeOverviewFutureRangeFilter,
    TPracticeOverviewProvidersReq,
    TPracticeOverviewRangeFilter,
    TPracticeOverviewReviewListReq,
    TPracticeOverviewTrendReq,
} from 'types/practiceOverview';

export const getTeamMemberRoles = () =>
    Request.get({
        url: '/schedule-server/provider/team-member/roles',
    });

export const getTeamMemberListPage = (params?: TTeamMemberListPageParams) =>
    Request.get({
        url: '/schedule-server/provider/team-member/listPage',
        params,
    });

export const getScopedProviderOptions = (params?: TScopedProviderOptionsParams) =>
    Request.get({
        url: '/schedule-server/provider/team-member/provider-options',
        params,
    });

export const addStaff = (data: TAddStaff) =>
    Request.post({
        url: '/schedule-server/provider/team-member/add-staff',
        params: data,
    });

export const getTeamMemberDetail = (id: number) =>
    Request.get({
        url: `/schedule-server/provider/team-member/${id}`,
    });

export const editStaff = (id: number, data: Partial<TAddStaff>) =>
    Request.put({
        url: `/schedule-server/provider/team-member/${id}/update-staff`,
        params: data,
    });

export const updateTeamMemberStatus = (id: number, status: string) =>
    Request.put({
        url: `/schedule-server/provider/team-member/${id}/status`,
        params: { status },
    });

export const pauseListing = (data: { providerId: number; pauseDays?: number }) =>
    Request.post({
        url: '/schedule-server/provider/team-member/pause-listing',
        params: data,
    });

export const resumeListing = (data: { providerId: number }) =>
    Request.post({
        url: '/schedule-server/provider/team-member/resume-listing',
        params: data,
    });

export const inviteProviders = (data: TInviteProvidersParams) =>
    Request.post({
        url: '/schedule-server/provider/team-member/invite-providers',
        params: data,
    });

export const checkProviderEmail = (email: string) =>
    Request.get({
        url: '/schedule-server/provider/team-member/check-provider-email',
        params: { email },
    });

export const checkStaffEmail = (email: string) =>
    Request.get({
        url: '/schedule-server/provider/team-member/check-staff-email',
        params: { email },
    });

export const switchProvider = (targetProviderId: number) =>
    Request.post({
        url: '/schedule-server/provider/switch/provider-preview',
        params: { targetProviderId },
    });

export const submitImportBatch = (fileUploadId: number) =>
    Request.post({
        url: '/schedule-server/provider/team-member/import-batches',
        params: { fileUploadId },
    });

export const submitWebsiteImportBatch = (websites: string[]) =>
    Request.post({
        url: '/schedule-server/provider/team-member/website-import-batches',
        params: { websites },
    });

export const getImportBatchDetail = (batchUid: string) =>
    Request.get({
        url: `/schedule-server/provider/team-member/import-batches/${batchUid}`,
    });

export const getImportBatchDrafts = (batchUid: string, params?: TImportDraftListParams) =>
    Request.get({
        url: `/schedule-server/provider/team-member/import-batches/${batchUid}/drafts`,
        params,
    });

export const updateImportDraft = (id: number, data: TUpdateImportDraftReq) =>
    Request.put({
        url: `/schedule-server/provider/team-member/import-drafts/${id}`,
        params: data,
    });

export const confirmImportBatch = (batchUid: string, data: TConfirmBatchParams) =>
    Request.post({
        url: `/schedule-server/provider/team-member/import-batches/${batchUid}/confirm`,
        params: data,
    });

export const sendTeamMemberInvite = (id: number) =>
    Request.post({
        url: `/schedule-server/provider/team-member/${id}/send-invite`,
    });

export const remindTeamMember = (id: number) =>
    Request.post({
        url: `/schedule-server/provider/team-member/${id}/remind`,
    });

export const updateUserPhone = (tel: string) =>
    Request.post({
        url: '/schedule-server/api/auth/user/phone',
        params: { tel },
    });

export const switchProviderIdentity = (targetProviderId: number) =>
    Request.post({
        url: '/schedule-server/provider/switch/provider',
        params: { targetProviderId },
    });

export const changePassword = (data: { oldPassword: string; newPassword: string }) =>
    Request.post({
        url: '/schedule-server/api/auth/changePassword',
        params: data,
    });

export const getPracticeList = () =>
    Request.get({
        url: '/schedule-server/api/auth/practiceList',
    });

export const getPracticePrescriptionList = (params: TPracticePrescriptionListFilter) =>
    Request.post({
        url: '/schedule-server/script/sure/practice/prescription/list',
        params,
    });

export const getReassignScenarios = (appointmentId: number) =>
    Request.post({
        url: `/schedule-server/provider/appointment/${appointmentId}/reassign-scenarios`,
    });

export const executeReassign = (appointmentId: number, params: TReassignExecuteReq) =>
    Request.post({
        url: `/schedule-server/provider/appointment/${appointmentId}/reassign`,
        params,
    });

export const getPracticeProviderOverviewList = (data: {
    compareTo?: string;
    current?: number;
    nameSearch?: string;
    orderKey?: string;
    orderType?: string;
    size?: number;
    sortField?: string;
    sortOrder?: string;
}) => Request.post({ url: '/schedule-server/provider/klarity/review/practice/provider/overview/list', params: data });

export const getPracticeReviewOverviewData = (cycle: string) =>
    Request.get({ url: `/schedule-server/provider/klarity/review/practice/summary/${cycle}` });

export const getPracticeScoreInfoData = () =>
    Request.get({ url: '/schedule-server/provider/klarity/review/practice/summary/info' });

export const getPracticeReviewList = (data: IGetKlarityReviewListInput & { providerId?: number }) =>
    Request.post({ url: '/schedule-server/provider/klarity/review/practice/list', params: data });

export const addPracticeReviewReply = (data: TKlarityReviewReplyInput) =>
    Request.post({
        url: '/schedule-server/provider/klarity/review/practice/comment/reply',
        params: data,
        disableErrorNote: true,
    });

export const getPracticeReviewReply = (providerId: number, reviewId: number) =>
    Request.get({
        url: `/schedule-server/provider/klarity/review/practice/comments/provider/${providerId}/${reviewId}`,
    });

export const submitPracticeDisputeReview = (data: IDisputeSubmitInput) =>
    Request.post({ url: '/schedule-server/provider/klarity/review/practice/dispute/submit', params: data });

export const getPracticeDisputeReviewDetail = (providerId: number, reviewId: number) =>
    Request.get({
        url: `/schedule-server/provider/klarity/review/practice/dispute/detail/provider/${providerId}/${reviewId}`,
    });

// GP Inbox APIs

export const gpGetConversationsList = (current: number, size: number) =>
    Request.get({
        url: '/schedule-server/provider/conversation/gp/list',
        params: { current, size },
    });

export const gpGetMyMessages = (current: number, size: number) =>
    Request.get({
        url: '/schedule-server/provider/conversation/gp/my-messages',
        params: { current, size },
    });

export const gpGetAllPatientMessages = (current: number, size: number) =>
    Request.get({
        url: '/schedule-server/provider/conversation/gp/all-patient-messages',
        params: { current, size },
    });

export const gpGetUnreadCount = () => Request.get({ url: '/schedule-server/provider/conversation/gp/unread' });

export const gpSendMessage = (params: TGPSendMessageParams) =>
    Request.post({
        url: '/schedule-server/provider/conversation/sendMessage',
        params,
    });

export const gpGetMessages = (params: TGPGetMessagesParams) =>
    Request.get({
        url: '/schedule-server/provider/conversation/messages',
        params,
    });

export const gpMarkRead = (conversationId: string) =>
    Request.post({
        url: '/schedule-server/provider/conversation/read-all',
        params: { conversationId },
    });

export const gpSearchPatientRecipient = (keyword: string) =>
    Request.get({
        url: '/schedule-server/provider/conversation/gp/recipient/patient',
        params: { keyword },
    });

export const gpSearchTeamMemberRecipient = (keyword: string) =>
    Request.get({
        url: '/schedule-server/provider/conversation/gp/recipient/team-member',
        params: { keyword },
    });

export const gpGetPatientConversations = (identityId: number) =>
    Request.get({
        url: '/schedule-server/provider/conversation/gp/patient/conversations',
        params: { identityId },
    });

export const getConversationsListWithIdentity = (current: number, size: number, identityId: number) =>
    Request.get({
        url: '/schedule-server/provider/conversations',
        params: { current, size, identityId },
    });

export const getUnreadAmountWithIdentity = (identityId: number) =>
    Request.get({
        url: '/schedule-server/provider/conversations/unread',
        params: { identityId },
    });

export type TGroupProspectListReq = {
    pageNo?: number;
    pageSize?: number;
    name?: string;
    email?: string;
    phone?: string;
    type?: string;
    on_boarding_status?: string[];
    include_sample?: boolean;
    tagNames?: string[];
    providerName?: string;
    orderKey?: string;
    orderType?: 'ASC' | 'DESC';
    status?: string;
    contactType?: string;
    // Insurance eligibility 多选筛选（identity-wide 语义）：EligibilityStatusEnum 全部值 + 两个 sentinel
    eligibilityStatus?: string[];
};

export const getGroupProspectList = (req: TGroupProspectListReq) =>
    Request.post<TGroupProspectListReq>({
        url: '/schedule-server/group/practice/prospect/list',
        params: {
            pageNo: req.pageNo ?? 1,
            pageSize: req.pageSize ?? 50,
            name: req.name,
            email: req.email,
            phone: req.phone,
            type: req.type,
            on_boarding_status: req.on_boarding_status,
            include_sample: req.include_sample ?? false,
            tagNames: req.tagNames,
            providerName: req.providerName,
            orderKey: req.orderKey ?? 'CREATE_AT',
            orderType: req.orderType ?? 'DESC',
            status: req.status,
            contactType: req.contactType,
            eligibilityStatus: req.eligibilityStatus,
        },
    });

export const getGroupProspectDetail = (identityId: number) =>
    Request.get({
        url: `/schedule-server/group/practice/prospect/identity/${identityId}`,
    });

export type TGroupPracticeClientAppointmentReq = {
    current?: number;
    size?: number;
    type?: string;
    status?: string;
    contactType?: string;
    startTime?: string;
    endTime?: string;
    orderKey?: string;
    orderType?: string;
};

export const getGPInvoiceList = (params: any) =>
    Request.get({
        url: '/schedule-server/group/practice/provider/invoice/client/list',
        params,
    });

export const getGPInvoiceDraftList = (params: any) =>
    Request.get({
        url: '/schedule-server/group/practice/provider/invoice/client/draftList',
        params,
    });

export const getGPInvoiceCounts = (clientId: number) =>
    Request.get({
        url: '/schedule-server/group/practice/provider/invoice/client/counts',
        params: { clientId },
    });

export const getGPTreatmentPlanCounts = (identityId: number) =>
    Request.get({
        url: '/schedule-server/group/practice/provider/treatment-plan/appointment/counts',
        params: { identityId },
        disableErrorNote: true,
    });

export const getGPTreatmentPlanItemList = (params: {
    clientId?: number;
    current?: number;
    size?: number;
    status?: string;
    orderKey?: string;
    orderType?: string;
}) =>
    Request.post({
        url: '/schedule-server/group/practice/provider/treatment-plan/appointment/pageList',
        params,
        disableErrorNote: true,
    });

export const getGroupPracticeClientAppointment = (identityId: number, params?: TGroupPracticeClientAppointmentReq) =>
    Request.post({
        url: `/schedule-server/group/practice/client/appointment/${identityId}/list`,
        params,
    });

export type TGroupPracticeTextVisitListReq = {
    clientId?: number;
    current?: number;
    size?: number;
    type?: string;
    providerId?: number;
    orderKey?: string;
    orderType?: string;
    assessmentName?: string;
    serviceTypeFilterMode?: string;
    status?: string;
};

export const getGroupPracticeTextVisitList = (params: TGroupPracticeTextVisitListReq) =>
    Request.post({
        url: '/schedule-server/group/practice/text/visit/patient/list',
        params,
    });

// #12 - Group-staff archive / status flip (identity-scoped)
export const gpUpdateProspectStatus = (params: { identityId: number; status: string }, overrideProviderId?: number) =>
    Request.put({
        url: '/schedule-server/group/practice/prospect/updateStatus',
        params,
        overrideProviderId,
    });

// #13 - Group-staff update prospect basic info (identity-scoped)
export const gpUpdateProspect = (identityId: number, item: Partial<import('types/common').Prospect>) =>
    Request.put({
        url: `/schedule-server/group/practice/prospect/update/${identityId}`,
        params: item,
    });

// #14 - Group-staff save patient health profile (identity-scoped)
export const gpUpdatePatientCondition = (identityId: number, params: any) =>
    Request.post({
        url: `/schedule-server/group/practice/prospect/profile/health/save/${identityId}`,
        params,
    });

// #15 - Group-staff save patient identity (identity-scoped)
export const gpSavePatientIdentity = (identityId: number, params: any) =>
    Request.post({
        url: `/schedule-server/group/practice/prospect/profile/identity/save/${identityId}`,
        params,
    });

// #16 - Group-staff candidate-tag pool for an identity
export const gpGetProspectTagCandidates = (identityId: number) =>
    Request.get({ url: `/schedule-server/group/practice/prospect/tag/candidates/${identityId}` });

// #17 - Group-staff list tags for an identity
export const gpGetProspectTagList = (identityId: number) =>
    Request.get({ url: `/schedule-server/group/practice/prospect/tag/list/${identityId}` });

// #18 - Group-staff assign tags identity-wide
export const gpAssignProspectTags = (params: { identityId: number; tagNames: string[] }) =>
    Request.post({
        url: '/schedule-server/group/practice/prospect/tag/assign',
        params,
    });

// #19 - Group-staff reorder tags identity-wide by tagName
export const gpReorderProspectTags = (identityId: number, items: { sortOrder: number; tagName: string }[]) =>
    Request.post({
        url: `/schedule-server/group/practice/prospect/tag/reorder/${identityId}`,
        params: items,
    });

// #20 - Group-staff soft-delete a tag identity-wide by name
export const gpDeleteProspectTag = (tagId: number) =>
    Request.delete({ url: `/schedule-server/group/practice/prospect/tag/${tagId}` });

export const gpGetProspectTagOptions = () =>
    Request.get({ url: '/schedule-server/group/practice/prospect/tag/options' });

export const getGPNoteAuthorGroups = (identityId: number) =>
    Request.get({
        url: `/schedule-server/group/practice/prospectNotes/groups/${identityId}`,
    });

export const getGPNoteListByAuthor = (identityId: number, authorId: number, authorType: string) =>
    Request.get({
        url: `/schedule-server/group/practice/prospectNotes/list/${identityId}`,
        params: { authorId, authorType },
    });

export const gpCreateProspectNote = (params: { identityId: number; notes: string }) =>
    Request.post({
        url: '/schedule-server/group/practice/prospectNotes/staff/create',
        params,
    });

export const getGPProspectForms = (identityId: number) =>
    Request.get({
        url: `/schedule-server/group/practice/prospect/form/list/${identityId}`,
    });

export const getGPDocumentList = (params: import('types/document').IGetDocumentListParams) =>
    Request.post({
        url: '/schedule-server/group/practice/document/page',
        params,
    });

export const getGPDocumentFilters = (identityId: number) =>
    Request.get({
        url: '/schedule-server/group/practice/document/filters',
        params: { identityId },
    });

export const gpSaveOrUpdateDocument = (params: import('types/document').ISaveOrUpdateDocument[], identityId: string) =>
    Request.post({
        url: `/schedule-server/group/practice/document/saveOrUpdate/${identityId}`,
        params,
    });

export const gpUploadDocument = (params: { file: File; identityId: number }) =>
    Request.post({
        url: '/schedule-server/group/practice/document/upload/for/identity',
        params,
        config: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

export const gpGetBillingEvents = (billingId: number) =>
    Request.get({
        url: `/schedule-server/group/practice/provider/billing/${billingId}/klarity-user-billing-events`,
    });

export const gpSendFollowUp = (params: { textVisitId: number; message: string }) =>
    Request.post({
        url: '/schedule-server/group/practice/text/visit/follow/up/send',
        params,
    });

export const getGPAppointmentCounts = (identityId: number) =>
    Request.get({
        url: `/schedule-server/group/practice/prospect/appointment/counts/${identityId}`,
    });

// ── Practice Overview APIs ──

export const getPracticeOverviewAiSummary = () =>
    Request.get({ url: '/schedule-server/group/practice/overview/ai-summary' });

export const getPracticeOverviewAttention = () =>
    Request.get({ url: '/schedule-server/group/practice/overview/attention' });

export const getPracticeOverviewAttentionDetail = (alertType: TAttentionAlertType) =>
    Request.get({ url: `/schedule-server/group/practice/overview/attention/${alertType}/details` });

export const dismissPracticeOverviewAttention = (alertType: TAttentionAlertType) =>
    Request.post({ url: `/schedule-server/group/practice/overview/attention/${alertType}/dismiss` });

export const getPracticeOverviewByDim = (params: TPracticeOverviewByDimReq) =>
    Request.post({ url: '/schedule-server/group/practice/overview/by-dim', params });

export const getPracticeOverviewFutureAppointments = (params: TPracticeOverviewFutureRangeFilter) =>
    Request.post({ url: '/schedule-server/group/practice/overview/future-appointments', params });

export const getPracticeOverviewFutureByDim = (params: TPracticeOverviewFutureByDimReq) =>
    Request.post({ url: '/schedule-server/group/practice/overview/future-appointments/by-dim', params });

export const getPracticeOverviewPeakHours = (params: TPracticeOverviewRangeFilter) =>
    Request.post({ url: '/schedule-server/group/practice/overview/peak-hours', params });

export const getPracticeOverviewProviders = (params: TPracticeOverviewProvidersReq) =>
    Request.post({ url: '/schedule-server/group/practice/overview/providers', params });

export const getPracticeOverviewReviews = (params: TPracticeOverviewRangeFilter) =>
    Request.post({ url: '/schedule-server/group/practice/overview/reviews', params });

export const getPracticeOverviewReviewList = (params: TPracticeOverviewReviewListReq) =>
    Request.post({ url: '/schedule-server/group/practice/overview/reviews/list', params });

export const getPracticeOverviewSummary = (params: TPracticeOverviewRangeFilter) =>
    Request.post({ url: '/schedule-server/group/practice/overview/summary', params });

export const getPracticeOverviewTrend = (params: TPracticeOverviewTrendReq) =>
    Request.post({ url: '/schedule-server/group/practice/overview/trend', params });
